import type { CodeableConcept, FamilyMemberHistory } from '@medplum/fhirtypes';

export const V3_ROLE_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-RoleCode';

export type FamilySide = 'maternal' | 'paternal';

/** Ordering group for the person view: parents, siblings, children, grandparents, aunts and
 * uncles, then everyone else, with unattributed history last. */
export type FamilyKinship = 'parent' | 'sibling' | 'child' | 'grandparent' | 'aunt-uncle' | 'other' | 'unspecified';

type SlotDefinition = {
  kinship: FamilyKinship;
  /** Degree of blood kinship. Absent for step, adoptive, foster and marital relations. */
  degree?: 1 | 2 | 3;
  side?: FamilySide;
  /** A relation a person has exactly one of, so every resource describing it is the same relative. */
  unique?: boolean;
};

/**
 * Canonical relatives, declared in display order within each kinship group. Only blood relatives
 * carry a degree; step, adoptive and foster relations say nothing about hereditary risk and are
 * never merged with the biological slot.
 */
export const RELATIVE_SLOTS = {
  mother: { kinship: 'parent', degree: 1, unique: true },
  father: { kinship: 'parent', degree: 1, unique: true },
  parent: { kinship: 'parent', degree: 1 },
  stepmother: { kinship: 'parent' },
  stepfather: { kinship: 'parent' },
  stepparent: { kinship: 'parent' },
  'adoptive-mother': { kinship: 'parent' },
  'adoptive-father': { kinship: 'parent' },
  'adoptive-parent': { kinship: 'parent' },
  'foster-mother': { kinship: 'parent' },
  'foster-father': { kinship: 'parent' },
  'foster-parent': { kinship: 'parent' },
  sister: { kinship: 'sibling', degree: 1 },
  brother: { kinship: 'sibling', degree: 1 },
  sibling: { kinship: 'sibling', degree: 1 },
  'twin-sister': { kinship: 'sibling', degree: 1 },
  'twin-brother': { kinship: 'sibling', degree: 1 },
  twin: { kinship: 'sibling', degree: 1 },
  'half-sister': { kinship: 'sibling', degree: 2 },
  'half-brother': { kinship: 'sibling', degree: 2 },
  'half-sibling': { kinship: 'sibling', degree: 2 },
  stepsister: { kinship: 'sibling' },
  stepbrother: { kinship: 'sibling' },
  stepsibling: { kinship: 'sibling' },
  daughter: { kinship: 'child', degree: 1 },
  son: { kinship: 'child', degree: 1 },
  child: { kinship: 'child', degree: 1 },
  stepdaughter: { kinship: 'child' },
  stepson: { kinship: 'child' },
  stepchild: { kinship: 'child' },
  'adopted-daughter': { kinship: 'child' },
  'adopted-son': { kinship: 'child' },
  'adopted-child': { kinship: 'child' },
  'foster-daughter': { kinship: 'child' },
  'foster-son': { kinship: 'child' },
  'foster-child': { kinship: 'child' },
  'maternal-grandmother': { kinship: 'grandparent', degree: 2, side: 'maternal', unique: true },
  'maternal-grandfather': { kinship: 'grandparent', degree: 2, side: 'maternal', unique: true },
  'maternal-grandparent': { kinship: 'grandparent', degree: 2, side: 'maternal' },
  'paternal-grandmother': { kinship: 'grandparent', degree: 2, side: 'paternal', unique: true },
  'paternal-grandfather': { kinship: 'grandparent', degree: 2, side: 'paternal', unique: true },
  'paternal-grandparent': { kinship: 'grandparent', degree: 2, side: 'paternal' },
  grandmother: { kinship: 'grandparent', degree: 2 },
  grandfather: { kinship: 'grandparent', degree: 2 },
  grandparent: { kinship: 'grandparent', degree: 2 },
  'great-grandmother': { kinship: 'grandparent', degree: 3 },
  'great-grandfather': { kinship: 'grandparent', degree: 3 },
  'great-grandparent': { kinship: 'grandparent', degree: 3 },
  'maternal-aunt': { kinship: 'aunt-uncle', degree: 2, side: 'maternal' },
  'maternal-uncle': { kinship: 'aunt-uncle', degree: 2, side: 'maternal' },
  'paternal-aunt': { kinship: 'aunt-uncle', degree: 2, side: 'paternal' },
  'paternal-uncle': { kinship: 'aunt-uncle', degree: 2, side: 'paternal' },
  aunt: { kinship: 'aunt-uncle', degree: 2 },
  uncle: { kinship: 'aunt-uncle', degree: 2 },
  'maternal-cousin': { kinship: 'other', degree: 3, side: 'maternal' },
  'paternal-cousin': { kinship: 'other', degree: 3, side: 'paternal' },
  cousin: { kinship: 'other', degree: 3 },
  niece: { kinship: 'other', degree: 2 },
  nephew: { kinship: 'other', degree: 2 },
  'niece-nephew': { kinship: 'other', degree: 2 },
  granddaughter: { kinship: 'other', degree: 2 },
  grandson: { kinship: 'other', degree: 2 },
  grandchild: { kinship: 'other', degree: 2 },
  spouse: { kinship: 'other' },
  /** A relation we recognise as a relative but have no slot for, shown under its source text. */
  other: { kinship: 'other' },
  /** "Family member", "other", unknown: real history with no relative attached. */
  unspecified: { kinship: 'unspecified', unique: true },
} as const satisfies Record<string, SlotDefinition>;

export type RelativeSlotKey = keyof typeof RELATIVE_SLOTS;

export const RELATIVE_SLOT_KEYS = Object.keys(RELATIVE_SLOTS) as RelativeSlotKey[];

/** English names, for hosts with no i18n (the AI context). User-facing labels live in `@liza/ui`. */
export const RELATIVE_SLOT_NAMES: Record<RelativeSlotKey, string> = {
  mother: 'Mother',
  father: 'Father',
  parent: 'Parent',
  stepmother: 'Stepmother',
  stepfather: 'Stepfather',
  stepparent: 'Stepparent',
  'adoptive-mother': 'Adoptive mother',
  'adoptive-father': 'Adoptive father',
  'adoptive-parent': 'Adoptive parent',
  'foster-mother': 'Foster mother',
  'foster-father': 'Foster father',
  'foster-parent': 'Foster parent',
  sister: 'Sister',
  brother: 'Brother',
  sibling: 'Sibling',
  'twin-sister': 'Twin sister',
  'twin-brother': 'Twin brother',
  twin: 'Twin',
  'half-sister': 'Half-sister',
  'half-brother': 'Half-brother',
  'half-sibling': 'Half-sibling',
  stepsister: 'Stepsister',
  stepbrother: 'Stepbrother',
  stepsibling: 'Stepsibling',
  daughter: 'Daughter',
  son: 'Son',
  child: 'Child',
  stepdaughter: 'Stepdaughter',
  stepson: 'Stepson',
  stepchild: 'Stepchild',
  'adopted-daughter': 'Adopted daughter',
  'adopted-son': 'Adopted son',
  'adopted-child': 'Adopted child',
  'foster-daughter': 'Foster daughter',
  'foster-son': 'Foster son',
  'foster-child': 'Foster child',
  'maternal-grandmother': 'Maternal grandmother',
  'maternal-grandfather': 'Maternal grandfather',
  'maternal-grandparent': 'Maternal grandparent',
  'paternal-grandmother': 'Paternal grandmother',
  'paternal-grandfather': 'Paternal grandfather',
  'paternal-grandparent': 'Paternal grandparent',
  grandmother: 'Grandmother',
  grandfather: 'Grandfather',
  grandparent: 'Grandparent',
  'great-grandmother': 'Great-grandmother',
  'great-grandfather': 'Great-grandfather',
  'great-grandparent': 'Great-grandparent',
  'maternal-aunt': 'Maternal aunt',
  'maternal-uncle': 'Maternal uncle',
  'paternal-aunt': 'Paternal aunt',
  'paternal-uncle': 'Paternal uncle',
  aunt: 'Aunt',
  uncle: 'Uncle',
  'maternal-cousin': 'Maternal cousin',
  'paternal-cousin': 'Paternal cousin',
  cousin: 'Cousin',
  niece: 'Niece',
  nephew: 'Nephew',
  'niece-nephew': 'Niece or nephew',
  granddaughter: 'Granddaughter',
  grandson: 'Grandson',
  grandchild: 'Grandchild',
  spouse: 'Spouse',
  other: 'Other relative',
  unspecified: 'Unspecified relative',
};

export type RelativeSex = 'male' | 'female';

/** A code whose slot depends on the relative's recorded sex. */
type SexedSlot = { female: RelativeSlotKey; male: RelativeSlotKey; unknown: RelativeSlotKey };

/** HL7 v3 RoleCode family-member codes. Sex-neutral codes resolve through the resource's `sex`. */
const ROLE_CODE_SLOTS: Record<string, RelativeSlotKey | SexedSlot> = {
  PRN: { female: 'mother', male: 'father', unknown: 'parent' },
  NPRN: { female: 'mother', male: 'father', unknown: 'parent' },
  MTH: 'mother',
  NMTH: 'mother',
  FTH: 'father',
  NFTH: 'father',
  STPPRN: { female: 'stepmother', male: 'stepfather', unknown: 'stepparent' },
  STPMTH: 'stepmother',
  STPFTH: 'stepfather',
  ADOPTP: { female: 'adoptive-mother', male: 'adoptive-father', unknown: 'adoptive-parent' },
  ADOPTM: 'adoptive-mother',
  ADOPTF: 'adoptive-father',
  PRNFOST: { female: 'foster-mother', male: 'foster-father', unknown: 'foster-parent' },
  MTHFOST: 'foster-mother',
  FTHFOST: 'foster-father',
  GESTM: 'other',
  SIB: { female: 'sister', male: 'brother', unknown: 'sibling' },
  NSIB: { female: 'sister', male: 'brother', unknown: 'sibling' },
  SIS: 'sister',
  NSIS: 'sister',
  BRO: 'brother',
  NBRO: 'brother',
  TWIN: { female: 'twin-sister', male: 'twin-brother', unknown: 'twin' },
  FTWIN: { female: 'twin-sister', male: 'twin-brother', unknown: 'twin' },
  ITWIN: { female: 'twin-sister', male: 'twin-brother', unknown: 'twin' },
  TWINSIS: 'twin-sister',
  FTWINSIS: 'twin-sister',
  ITWINSIS: 'twin-sister',
  TWINBRO: 'twin-brother',
  FTWINBRO: 'twin-brother',
  ITWINBRO: 'twin-brother',
  HSIB: { female: 'half-sister', male: 'half-brother', unknown: 'half-sibling' },
  HSIS: 'half-sister',
  HBRO: 'half-brother',
  STPSIB: { female: 'stepsister', male: 'stepbrother', unknown: 'stepsibling' },
  STPSIS: 'stepsister',
  STPBRO: 'stepbrother',
  CHILD: { female: 'daughter', male: 'son', unknown: 'child' },
  NCHILD: { female: 'daughter', male: 'son', unknown: 'child' },
  DAU: 'daughter',
  DAUC: 'daughter',
  SON: 'son',
  SONC: 'son',
  STPCHLD: { female: 'stepdaughter', male: 'stepson', unknown: 'stepchild' },
  STPDAU: 'stepdaughter',
  STPSON: 'stepson',
  CHLDADOPT: { female: 'adopted-daughter', male: 'adopted-son', unknown: 'adopted-child' },
  DAUADOPT: 'adopted-daughter',
  SONADOPT: 'adopted-son',
  CHLDFOST: { female: 'foster-daughter', male: 'foster-son', unknown: 'foster-child' },
  DAUFOST: 'foster-daughter',
  SONFOST: 'foster-son',
  GRPRN: { female: 'grandmother', male: 'grandfather', unknown: 'grandparent' },
  GRMTH: 'grandmother',
  GRFTH: 'grandfather',
  MGRPRN: { female: 'maternal-grandmother', male: 'maternal-grandfather', unknown: 'maternal-grandparent' },
  MGRMTH: 'maternal-grandmother',
  MGRFTH: 'maternal-grandfather',
  PGRPRN: { female: 'paternal-grandmother', male: 'paternal-grandfather', unknown: 'paternal-grandparent' },
  PGRMTH: 'paternal-grandmother',
  PGRFTH: 'paternal-grandfather',
  GGRPRN: { female: 'great-grandmother', male: 'great-grandfather', unknown: 'great-grandparent' },
  GGRMTH: 'great-grandmother',
  GGRFTH: 'great-grandfather',
  MGGRPRN: { female: 'great-grandmother', male: 'great-grandfather', unknown: 'great-grandparent' },
  MGGRMTH: 'great-grandmother',
  MGGRFTH: 'great-grandfather',
  PGGRPRN: { female: 'great-grandmother', male: 'great-grandfather', unknown: 'great-grandparent' },
  PGGRMTH: 'great-grandmother',
  PGGRFTH: 'great-grandfather',
  AUNT: 'aunt',
  MAUNT: 'maternal-aunt',
  PAUNT: 'paternal-aunt',
  UNCLE: 'uncle',
  MUNCLE: 'maternal-uncle',
  PUNCLE: 'paternal-uncle',
  COUSN: 'cousin',
  MCOUSN: 'maternal-cousin',
  PCOUSN: 'paternal-cousin',
  NIENEPH: { female: 'niece', male: 'nephew', unknown: 'niece-nephew' },
  NIECE: 'niece',
  NEPHEW: 'nephew',
  GRNDCHILD: { female: 'granddaughter', male: 'grandson', unknown: 'grandchild' },
  GRNDDAU: 'granddaughter',
  GRNDSON: 'grandson',
  SPS: 'spouse',
  HUSB: 'spouse',
  WIFE: 'spouse',
  DOMPART: 'spouse',
  SIGOTHR: 'spouse',
  PRNINLAW: 'other',
  MTHINLAW: 'other',
  FTHINLAW: 'other',
  SIBINLAW: 'other',
  SISINLAW: 'other',
  BROINLAW: 'other',
  CHLDINLAW: 'other',
  DAUINLAW: 'other',
  SONINLAW: 'other',
  FAMMEMB: 'unspecified',
  EXT: 'unspecified',
  UNK: 'unspecified',
  OTH: 'unspecified',
};

export const normalizeRelationshipText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Relationship text as sources write it, for resources whose coding is not a v3 RoleCode (Epic
 * local codes) or carries no coding at all. Keys are normalised: lower case, no punctuation.
 */
const RELATIONSHIP_TEXT_SLOTS: Record<string, RelativeSlotKey | SexedSlot> = {
  ...Object.fromEntries(
    Object.entries(RELATIVE_SLOT_NAMES).map(([key, name]) => [normalizeRelationshipText(name), key]),
  ),
  'natural mother': 'mother',
  'birth mother': 'mother',
  'biological mother': 'mother',
  mom: 'mother',
  'natural father': 'father',
  'birth father': 'father',
  'biological father': 'father',
  dad: 'father',
  'natural parent': { female: 'mother', male: 'father', unknown: 'parent' },
  'biological parent': { female: 'mother', male: 'father', unknown: 'parent' },
  'natural sister': 'sister',
  'natural brother': 'brother',
  'natural sibling': { female: 'sister', male: 'brother', unknown: 'sibling' },
  'natural daughter': 'daughter',
  'natural son': 'son',
  'natural child': { female: 'daughter', male: 'son', unknown: 'child' },
  'maternal grandma': 'maternal-grandmother',
  'maternal grandpa': 'maternal-grandfather',
  'paternal grandma': 'paternal-grandmother',
  'paternal grandpa': 'paternal-grandfather',
  grandma: 'grandmother',
  grandpa: 'grandfather',
  husband: 'spouse',
  wife: 'spouse',
  partner: 'spouse',
  'family member': 'unspecified',
  'other family member': 'unspecified',
  relative: 'unspecified',
  other: 'unspecified',
  unknown: 'unspecified',
  unspecified: 'unspecified',
};

export type RelativeSlot = SlotDefinition & {
  key: RelativeSlotKey;
  unique: boolean;
  /** For the `other` slot: the relationship as the source wrote it, in place of the slot name. */
  text?: string;
};

const resolveSexed = (entry: RelativeSlotKey | SexedSlot, sex: RelativeSex | undefined): RelativeSlotKey => {
  if (typeof entry === 'string') return entry;
  if (sex === 'female') return entry.female;
  if (sex === 'male') return entry.male;
  return entry.unknown;
};

/** The relative's sex from `FamilyMemberHistory.sex`: administrative-gender codes or free text. */
export const relativeSex = (sex: CodeableConcept | undefined): RelativeSex | undefined => {
  const values = [...(sex?.coding ?? []).flatMap((c) => [c.code, c.display]), sex?.text]
    .filter((v): v is string => Boolean(v))
    .map((v) => v.trim().toLowerCase());
  if (values.some((v) => v === 'female' || v === 'f')) return 'female';
  if (values.some((v) => v === 'male' || v === 'm')) return 'male';
  return undefined;
};

/** The canonical slot for a v3 RoleCode, or `undefined` for a code the table does not know. */
export const relativeSlotKeyForCode = (code: string, sex?: RelativeSex): RelativeSlotKey | undefined => {
  const entry = ROLE_CODE_SLOTS[code.toUpperCase()];
  return entry === undefined ? undefined : resolveSexed(entry, sex);
};

const toSlot = (key: RelativeSlotKey, text?: string): RelativeSlot => {
  const definition: SlotDefinition = RELATIVE_SLOTS[key];
  return { ...definition, key, unique: definition.unique ?? false, text: key === 'other' ? text : undefined };
};

/**
 * The canonical relative a resource describes. A v3 RoleCode wins; a coding from another system
 * (Epic's local relationship codes) or no coding at all falls back to the relationship text. A
 * relationship the tables do not recognise is `other`, keeping its source text as the label; no
 * usable relationship at all is `unspecified`.
 */
export const relativeSlot = (history: FamilyMemberHistory): RelativeSlot => {
  const sex = relativeSex(history.sex);
  const codings = history.relationship?.coding ?? [];

  for (const coding of codings) {
    if (coding.system !== V3_ROLE_CODE_SYSTEM || !coding.code) continue;
    const key = relativeSlotKeyForCode(coding.code, sex);
    if (key) return toSlot(key);
  }

  const texts = [history.relationship?.text, ...codings.map((c) => c.display)].filter((v): v is string =>
    Boolean(v?.trim()),
  );
  for (const text of texts) {
    const entry = RELATIONSHIP_TEXT_SLOTS[normalizeRelationshipText(text)];
    if (entry !== undefined) return toSlot(resolveSexed(entry, sex));
  }

  // A code from an unknown system with no text at all says nothing about who the relative is.
  const sourceText = texts[0] ?? codings.find((c) => c.code && !c.system)?.code;
  return sourceText ? toSlot('other', sourceText.trim()) : toSlot('unspecified');
};

const KINSHIP_ORDER: FamilyKinship[] = [
  'parent',
  'sibling',
  'child',
  'grandparent',
  'aunt-uncle',
  'other',
  'unspecified',
];

/** Person-view order: kinship group, then the slot's declared position within it. */
export const compareRelativeSlots = (a: RelativeSlot, b: RelativeSlot): number =>
  KINSHIP_ORDER.indexOf(a.kinship) - KINSHIP_ORDER.indexOf(b.kinship) ||
  RELATIVE_SLOT_KEYS.indexOf(a.key) - RELATIVE_SLOT_KEYS.indexOf(b.key) ||
  (a.text ?? '').localeCompare(b.text ?? '');
